import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import type { FileItem } from '@app/core/services/file.service';
import { firstValueFrom, from, Observable, switchMap } from 'rxjs';
import type { DeleteFileParams, DownloadFileParams, FileConversionStatus, FileConversionStatusParams, FileDetails, FileServerResponse, GetFileDetailsParams, OfficeDocumentViewType, UploadFileParams, WopiConvertTarget, WopiFileInfo, WopiToken } from './file-server.types';
import { buildOfficeDocumentUrl, createFileDetailsRequest, createFileUploadFormData, encodeDownloadFileName, ensureContentType, normalizeFileServerUrl, prepareFileDetails, unwrapResponse } from './file-server.utils';

/**
 * Native Angular transport for gradually migrated file-server operations.
 * Uses HttpClient so existing interceptors supply request context. Callers own loading UI,
 * failure presentation, document-archive updates and conversion polling.
 * Call after AppSettingsService.ensureIsReady() when using configured server settings.
 * Promise-based methods start their HTTP request immediately and do not expose cancellation.
 * Type-only references to FileService contracts do not load the legacy service at runtime.
 */
@Injectable({
    providedIn: 'root',
})
export class FileServerService {
    constructor(
        private http: HttpClient,
        private appSettings: AppSettingsService,
    ) {}

    /**
     * Fetches file content and repairs missing or generic image MIME types when possible.
     *
     * @param url Complete or application-relative download URL.
     * @returns An observable of the blob; HTTP failures are emitted to the subscriber.
     * Unsubscribing cancels the HTTP request. The caller owns any created object URLs.
     */
    public getBlob(url: string): Observable<Blob> {
        return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(switchMap((response) => from(ensureContentType(response.body ?? new Blob(), response.headers.get('Content-Type'), response.headers.get('Content-Disposition'), url))));
    }

    /**
     * Downloads stored file bytes through HttpClient and the existing request interceptors.
     * Does not open a browser tab, save to disk or display loading/error UI.
     *
     * @param params Unencoded server filename and optional server root override.
     * @returns File content as an ArrayBuffer, suitable for PDF merging.
     * @throws Error for a missing server URL; HTTP failures reject the promise.
     */
    public downloadFile(params: DownloadFileParams): Promise<ArrayBuffer> {
        const name = encodeURIComponent(encodeDownloadFileName(params.fileName));
        return firstValueFrom(this.http.get(`${this.getFileServerUrl(params.url)}/api/file/download?name=${name}`, { responseType: 'arraybuffer' }));
    }

    /**
     * Deletes stored content without confirmation or loading UI.
     * Callers must separately remove its archive record after this succeeds.
     *
     * @param params Unencoded server filename and optional server root override.
     * @returns A promise that resolves after deletion.
     * @throws Error for a missing server URL or server error response; HTTP failures reject.
     */
    public async deleteFile(params: DeleteFileParams): Promise<void> {
        const response = await firstValueFrom(this.http.post<unknown>(`${this.getFileServerUrl(params.url)}/api/file/delete`, { fileName: params.fileName }));
        unwrapResponse(response, 'File deletion failed');
    }

    /**
     * Uploads one browser file using the legacy multipart endpoint.
     *
     * @param params File, replacement policy, dimensions and optional filename templates.
     * @returns The uploaded file record, without linking it to a document-archive record.
     * @throws Error for a missing server URL or an error response; HTTP failures reject the promise.
     */
    public async uploadFile(params: UploadFileParams): Promise<FileItem> {
        const url = `${this.getFileServerUrl(params.url)}/api/file/upload`;
        const response = await firstValueFrom(this.http.post<FileItem | FileServerResponse<FileItem>>(url, createFileUploadFormData(params)));
        return unwrapResponse<FileItem>(response, 'File upload failed');
    }

    /**
     * Retrieves file metadata and viewer/download links by stored filename or file key number.
     * Rewrites image and thumbnail links through the configured proxy with the client session ID.
     *
     * @param params File identifier, optional dimensions, waiting policy and correlation ID.
     * @returns File details, or null without making a request when neither identifier is provided.
     * @throws Error containing the server failure message; HTTP failures reject the promise.
     */
    public async getFileDetails(params: GetFileDetailsParams): Promise<FileDetails | null> {
        if ((!params.fileItemKeyno || params.fileItemKeyno < 1) && !params.fileName) return null;

        const response = await firstValueFrom(this.http.post<FileDetails | FileServerResponse<FileDetails>>(`${this.getFileServerUrl(params.url)}/api/file/getfiledetails`, createFileDetailsRequest(params)));

        const linkOptions = {
            fileServerUrl: this.appSettings.settings.fileServerUrl,
            proxyId: this.appSettings.settings.fileServerProxyId,
            clientSessionId: this.appSettings.clientSessionId || this.appSettings.settings.clientSessionId || '',
        };
        return prepareFileDetails(response, linkOptions, params.instanceId);
    }

    /**
     * Authorizes access to an Office document through WOPI.
     *
     * @param documentId File-server document identifier.
     * @returns Access token and optional expiry supplied by the server.
     * @throws Error for a server error response; HTTP failures reject the promise.
     */
    public async getWopiToken(documentId: string): Promise<WopiToken> {
        const response = await firstValueFrom(
            this.http.post<WopiToken | FileServerResponse<WopiToken>>(`${this.getFileServerUrl()}/wopi/authorize`, {
                container: '',
                documentId,
            }),
        );

        return unwrapResponse(response);
    }

    /**
     * Retrieves WOPI metadata used to determine Office editing and conversion availability.
     *
     * @param documentId File-server document identifier, encoded as a single URL segment.
     * @returns WOPI file information.
     * @throws Error for a server error response; HTTP failures reject the promise.
     */
    public async getWopiFileInfo(documentId: string): Promise<WopiFileInfo> {
        const response = await firstValueFrom(this.http.get<WopiFileInfo | FileServerResponse<WopiFileInfo>>(`${this.getFileServerUrl()}/api/files/getfileinfo/${encodeURIComponent(documentId)}`));

        return unwrapResponse(response);
    }

    /**
     * Retrieves pre-conversion information, including whether the target document already exists.
     * Opening the convert host page is a separate operation owned by the viewer.
     *
     * @param documentId Source document identifier.
     * @returns Target existence, identifier and extension.
     * @throws Error for a server error response; HTTP failures reject the promise.
     */
    public async checkIfConvertTargetExists(documentId: string): Promise<WopiConvertTarget> {
        const response = await firstValueFrom(this.http.get<WopiConvertTarget | FileServerResponse<WopiConvertTarget>>(`${this.getFileServerUrl()}/api/files/preconvert/${encodeURIComponent(documentId)}`));

        return unwrapResponse(response);
    }

    /**
     * Checks Office conversion status once. The caller controls when to request another check;
     * stopping polling does not cancel an already-running promise-based request.
     *
     * @param params Document ID and current file key number.
     * @returns Completion status and the resulting filename/key for subsequent checks.
     * @throws Error for a server error response; HTTP failures reject the promise.
     */
    public async isConvertCompleted(params: FileConversionStatusParams): Promise<FileConversionStatus> {
        const response = await firstValueFrom(this.http.post<FileConversionStatus | FileServerResponse<FileConversionStatus>>(`${this.getFileServerUrl()}/api/file/isconvertcompleted`, params));

        return unwrapResponse(response);
    }

    /**
     * Builds the Office host-page URL without making a request.
     * Office permissions and editor customization are controlled by the file-server host page,
     * not by this URL builder. The view mode alone does not configure collaboration prompts.
     *
     * @param documentId File-server document identifier.
     * @param viewType Host-page mode: view, edit or convert.
     * @param accessToken WOPI access token.
     * @param clientSessionId Session ID to include in the URL.
     * @returns Encoded host-page URL using the current browser origin for Office messages.
     * @throws Error when no file-server URL is configured.
     */
    public getOfficeDocumentUrl(documentId: string, viewType: OfficeDocumentViewType, accessToken: string, clientSessionId: string): string {
        return buildOfficeDocumentUrl(this.getFileServerUrl(), documentId, viewType, accessToken, clientSessionId, window.location.origin);
    }

    /** Resolves a caller override or the configured server root, then validates and normalizes it. */
    private getFileServerUrl(fileServerUrl?: string): string {
        return normalizeFileServerUrl(fileServerUrl || this.appSettings.settings.fileServerUrl);
    }
}
